import { apiClient } from '@/lib/api-client';
import type { AuditEvent, Clinic, Role, User } from '@/types/api';

export type UserInput = {
  name: string;
  email: string;
  password: string;
  role: Role;
  treatsPatients?: boolean;
  licenseNumber?: string | null;
  doctorIds?: string[];
};

export type UserPatch = Partial<{
  name: string;
  role: Role;
  treatsPatients: boolean;
  licenseNumber: string | null;
  active: boolean;
  password: string;
  doctorIds: string[];
}>;

export type ClinicPatch = Partial<Omit<Clinic, 'id' | 'logoVersion'>>;

export const settingsApi = {
  async clinic() {
    const { data } = await apiClient.get<Clinic>('/clinic');
    return data;
  },
  async updateClinic(patch: ClinicPatch) {
    const { data } = await apiClient.patch<Clinic>('/clinic', patch);
    return data;
  },
  /** The logo as an object URL (the request needs the auth header, so no plain <img src>). */
  async logoUrl(version: string) {
    const { data } = await apiClient.get<Blob>('/clinic/logo', {
      params: { v: version },
      responseType: 'blob',
    });
    return URL.createObjectURL(data);
  },
  async setLogo(type: string, base64: string) {
    const { data } = await apiClient.put<Clinic>('/clinic/logo', { type, data: base64 });
    return data;
  },
  async removeLogo() {
    const { data } = await apiClient.delete<Clinic>('/clinic/logo');
    return data;
  },
  async users() {
    const { data } = await apiClient.get<User[]>('/users');
    return data;
  },
  async createUser(input: UserInput) {
    const { data } = await apiClient.post<User>('/users', input);
    return data;
  },
  async updateUser(id: string, patch: UserPatch) {
    const { data } = await apiClient.patch<User>(`/users/${id}`, patch);
    return data;
  },
  async audit(params: { patientId?: string; entityId?: string; limit?: number; before?: string }) {
    const { data } = await apiClient.get<AuditEvent[]>('/audit', { params });
    return data;
  },
};
